import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { lojaApi } from '../../services/api';
import { LogoMark, Loading } from '../../components/ui';
import { formatarMoeda } from '../../lib/format';
import { useCarrinho } from './carrinho';
import { ConfiguradorModal } from './ConfiguradorModal';
import { CarrinhoDrawer } from './CarrinhoDrawer';
import {
  ShoppingBag, Shirt, Palette, Ruler, Truck, ShieldCheck,
  Sparkles, ArrowRight, Lock, User,
} from 'lucide-react';

export function LojaPage() {
  const { quantidadeTotal, setAberto } = useCarrinho();
  const [produtoSel, setProdutoSel] = useState<any>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['loja-catalogo'],
    queryFn: () => lojaApi.catalogo().then(r => r.data),
  });

  const produtos = data?.produtos || [];
  const cores = data?.cores || [];
  const tamanhos = data?.tamanhos || [];
  const estampas = data?.estampas || [];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-md border-b border-ink-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/loja" className="flex items-center gap-2.5">
            <LogoMark size={32} />
            <span className="font-display font-bold text-ink-950 tracking-tight">MONCHERY</span>
          </Link>
          <nav className="hidden md:flex items-center gap-7 text-sm font-medium text-ink-500">
            <a href="#produtos" className="hover:text-ink-900 transition-colors">Produtos</a>
            <a href="#estampas" className="hover:text-ink-900 transition-colors">Estampas</a>
            <a href="#como-funciona" className="hover:text-ink-900 transition-colors">Como funciona</a>
          </nav>
          <div className="flex items-center gap-2">
            <Link
              to="/loja/conta"
              className="btn-secondary !rounded-full !px-4"
              title="Minha conta"
            >
              <User className="w-4 h-4" />
              <span className="hidden sm:inline">Minha conta</span>
            </Link>
            <button
              onClick={() => setAberto(true)}
              className="relative btn-secondary !rounded-full !px-4"
            >
              <ShoppingBag className="w-4 h-4" />
              <span className="hidden sm:inline">Carrinho</span>
              {quantidadeTotal > 0 && (
                <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-brand-600 text-white text-[10px] font-bold flex items-center justify-center">
                  {quantidadeTotal}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative bg-navy-950 overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-brand-600/30 blur-[130px]" />
        <div className="absolute bottom-0 -left-24 w-80 h-80 rounded-full bg-brand-500/15 blur-[110px]" />
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              'linear-gradient(white 1px, transparent 1px), linear-gradient(90deg, white 1px, transparent 1px)',
            backgroundSize: '44px 44px',
          }}
        />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-20 sm:py-28">
          <div className="max-w-2xl">
            <span className="badge bg-brand-500/15 text-brand-300 ring-1 ring-inset ring-brand-400/30 mb-6">
              <Sparkles className="w-3 h-3" /> Produção própria · Personalização online
            </span>
            <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-white leading-[1.08] tracking-tight">
              Sua camiseta,
              <br />
              <span className="text-brand-400">do seu jeito.</span>
            </h1>
            <p className="text-lg text-ink-300 mt-5 max-w-lg">
              Escolha o modelo, a cor, o tamanho e a estampa. A gente produz com carinho
              e entrega no prazo combinado.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <a href="#produtos" className="btn-primary !py-3.5 !px-6 !text-[15px] !rounded-xl">
                <Shirt className="w-5 h-5" />
                Montar a minha
              </a>
              <a href="#estampas" className="btn !py-3.5 !px-6 !text-[15px] !rounded-xl bg-white/10 text-white hover:bg-white/15 border border-white/10">
                <Palette className="w-5 h-5" />
                Ver estampas
              </a>
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-2 mt-10 text-[13px] text-ink-400">
              <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-brand-400" /> PIX e cartão</span>
              <span className="flex items-center gap-2"><Ruler className="w-4 h-4 text-brand-400" /> P ao XG</span>
              <span className="flex items-center gap-2"><Truck className="w-4 h-4 text-brand-400" /> Prazo combinado na produção</span>
            </div>
          </div>
        </div>
      </section>

      {/* Produtos */}
      <section id="produtos" className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20 scroll-mt-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-ink-950 tracking-tight">
              Escolha o modelo
            </h2>
            <p className="text-ink-500 mt-2">Depois personalize com cor, tamanho e estampa.</p>
          </div>
        </div>

        {isLoading ? (
          <Loading />
        ) : produtos.length === 0 ? (
          <div className="card text-center py-14">
            <p className="text-ink-500">Nenhum produto disponível no momento.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {produtos.map((p: any) => (
              <div
                key={p.id}
                className="group bg-white rounded-2xl border border-ink-200/70 shadow-card overflow-hidden transition-all duration-200 hover:shadow-pop hover:-translate-y-1"
              >
                {/* Imagem */}
                <div className="aspect-[4/3] bg-ink-100 relative overflow-hidden">
                  {p.imagem ? (
                    <img
                      src={p.imagem}
                      alt={p.nome}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-ink-100 to-ink-200 flex items-center justify-center">
                      <Shirt className="w-16 h-16 text-ink-300" />
                    </div>
                  )}
                  {p.precoPromocional && (
                    <span className="absolute top-3 left-3 badge bg-red-600 text-white shadow-md">
                      Oferta
                    </span>
                  )}
                </div>

                {/* Info */}
                <div className="p-5">
                  <h3 className="font-display font-semibold text-ink-950">{p.nome}</h3>
                  {p.descricao && (
                    <p className="text-[13px] text-ink-500 mt-1 line-clamp-2">{p.descricao}</p>
                  )}
                  <div className="flex items-end justify-between mt-4">
                    <div>
                      {p.precoPromocional ? (
                        <>
                          <p className="text-xs text-ink-400 line-through">{formatarMoeda(p.precoBase)}</p>
                          <p className="font-display text-lg font-bold text-ink-950">
                            {formatarMoeda(p.precoPromocional)}
                          </p>
                        </>
                      ) : (
                        <>
                          <p className="text-xs text-ink-400">A partir de</p>
                          <p className="font-display text-lg font-bold text-ink-950">
                            {formatarMoeda(p.precoBase)}
                          </p>
                        </>
                      )}
                    </div>
                    <button
                      onClick={() => setProdutoSel(p)}
                      className="btn-primary !py-2.5 !px-4 text-[13px] !rounded-xl"
                    >
                      Personalizar
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Estampas */}
      <section id="estampas" className="bg-ink-50/60 border-y border-ink-100 scroll-mt-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <div className="text-center mb-10">
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-ink-950 tracking-tight">
              Estampas disponíveis
            </h2>
            <p className="text-ink-500 mt-2">Escolha uma estampa ou peça a sua ideia nas observações do pedido.</p>
          </div>
          {estampas.length === 0 ? (
            <p className="text-center text-ink-400">Novas estampas em breve.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {estampas.map((e: any) => (
                <div
                  key={e.id}
                  className="group bg-white rounded-2xl border border-ink-200/70 shadow-card overflow-hidden transition-all duration-200 hover:shadow-pop hover:-translate-y-1"
                >
                  <div className="aspect-square bg-ink-100 overflow-hidden">
                    {e.imagem ? (
                      <img
                        src={e.imagem}
                        alt={e.nome}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-brand-100 to-brand-200 flex items-center justify-center p-4">
                        <span className="font-display font-bold text-brand-700 text-center">{e.nome}</span>
                      </div>
                    )}
                  </div>
                  <div className="p-3.5 flex items-center justify-between gap-2">
                    <p className="text-[13px] font-semibold text-ink-800 truncate">{e.nome}</p>
                    {Number(e.precoAdicional) > 0 && (
                      <p className="text-xs font-bold text-brand-600 whitespace-nowrap">
                        +{formatarMoeda(e.precoAdicional)}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Como funciona */}
      <section id="como-funciona" className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20 scroll-mt-16">
        <div className="text-center mb-12">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-ink-950 tracking-tight">
            Como funciona
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          {[
            { icon: Shirt, titulo: '1. Monte sua peça', texto: 'Escolha o modelo, a cor, o tamanho e a estampa direto aqui no site.' },
            { icon: ShieldCheck, titulo: '2. Pague com segurança', texto: 'PIX ou cartão de crédito. Seu pedido entra na fila assim que o pagamento é confirmado.' },
            { icon: Truck, titulo: '3. Receba sua peça', texto: 'Produzimos e combinamos a entrega ou retirada com você.' },
          ].map(({ icon: Icon, titulo, texto }) => (
            <div key={titulo} className="card text-center p-8">
              <div className="w-14 h-14 rounded-2xl bg-brand-50 flex items-center justify-center mx-auto mb-5">
                <Icon className="w-7 h-7 text-brand-600" />
              </div>
              <h3 className="font-display font-semibold text-ink-950">{titulo}</h3>
              <p className="text-sm text-ink-500 mt-2">{texto}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-navy-950 text-navy-300">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2.5">
            <LogoMark size={30} />
            <span className="font-display font-bold text-white tracking-tight">MONCHERY</span>
          </div>
          <p className="text-2xs text-ink-500">
            © {new Date().getFullYear()} Monchery — Camisetas personalizadas
          </p>
          <Link
            to="/login"
            className="flex items-center gap-1.5 text-2xs font-semibold text-ink-500 hover:text-white transition-colors"
          >
            <Lock className="w-3.5 h-3.5" />
            Área administrativa
          </Link>
        </div>
      </footer>

      {/* Configurador + Carrinho */}
      <ConfiguradorModal
        produto={produtoSel}
        cores={cores}
        tamanhos={tamanhos}
        estampas={estampas}
        aberto={!!produtoSel}
        onFechar={() => setProdutoSel(null)}
      />
      <CarrinhoDrawer />
    </div>
  );
}
